import {
  Component, Input, Output, EventEmitter,
  OnInit, OnChanges, SimpleChanges,
  ChangeDetectorRef, HostListener, inject
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { VendaV2Service } from '../../../services/venda-v2.service';
import { ParametrosService } from '../../../services/parametros.service';
import { AuthService } from '../../../services/auth.service';
import { DocumentoClienteModal } from '../documento-cliente-modal/documento-cliente-modal';
import { OrcamentoDTO, VendaDetalheDTO } from '../../../models/financeiro.model';
import { ErrorModel } from '../../../models/error.model';
import {
  EfetuarVendaDTO,
  BandeiraCartaoV2,
  BANDEIRAS,
  FORMAS_A_VISTA,
  FORMAS_PARCELADA,
  FORMAS_VENDA_MISTA,
  FORMAS_COM_BANDEIRA,
  FORMAS_PARCELADAS,
  TIPO_VENDA_A_VISTA,
  TIPO_VENDA_PARCELADA,
  TIPO_VENDA_MISTA,
  FORMA_CREDITO_PARCELADO,
  ERRO_CPF_CNPJ_INVALIDO,
  ERRO_CLIENTE_RESTRICAO,
  ERRO_ACAO_NAO_PERMITIDA,
  ERRO_FUNCAO_NAO_IMPL,
  ERRO_ADICIONAR_PAGAMENTO,
  ERRO_EFETIVAR_VENDA,
  ERRO_BAIXAR_PARCELA,
  ResumoVendaV2,
} from '../../../models/venda-v2.model';
import { todayLocalDateString, localDateStringPlusDays } from '../../../core/utils/date.util';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';

// Tipos de ação do modal (alinhado com venda-modal.ts existente)
export type TipoAcaoV2 = 'NOVA_VENDA' | 'BAIXAR_PARCELA';

// Passo do wizard
type FlowStep = 'tipo' | 'forma' | 'parcelas' | 'bandeira' | 'entrada' | 'confirmacao';

interface ProgressItem {
  key: FlowStep;
  label: string;
}

// Opção de tipo de venda exibida no passo 1
interface TipoVendaOpcao {
  value: string;  // TIPO_VENDA_* constant
  label: string;
  descricao: string;
  desativado: boolean;
  tooltipDesativado?: string;
}

// Opção de forma de pagamento exibida no passo 2
interface FormaOpcao {
  value: string;
  label: string;
}

/** Categoria em parametros.parametros com os limites do prazo de entrega (valorMinimo/valorMaximo, em dias). */
const CATEGORIA_PARAMETRO_VENDAS = 'PARAMETRO_VENDAS';
/** Limites usados enquanto o parâmetro não carrega, ou se ele estiver ausente/inválido. */
const PRAZO_MINIMO_PADRAO = 2;
const PRAZO_MAXIMO_PADRAO = 59;

@Component({
  selector: 'app-venda-modal-v2',
  standalone: true,
  imports: [CommonModule, FormsModule, CurrencyPipe, DocumentoClienteModal],
  templateUrl: './venda-modal-v2.html',
  styleUrl: './venda-modal-v2.css'
})
export class VendaModalV2 implements OnInit, OnChanges {

  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event): void {
    if (!this.isOpen || this.isLoading) return;
    event.preventDefault();
    this.fecharModal(false);
  }

  // ── Inputs ───────────────────────────────────────────────────────────────
  /** Controla a visibilidade do modal */
  @Input() isOpen = false;

  /** Modo de operação */
  @Input() tipoAcao: TipoAcaoV2 = 'NOVA_VENDA';

  /**
   * NOVA_VENDA: orçamento de origem.
   * Campos usados: id (idOrcamento), idCliente, idVendedor, nomeCliente, valorTotal.
   */
  @Input() orcamento!: OrcamentoDTO;

  /**
   * BAIXAR_PARCELA: venda existente.
   * Campos usados: idOrcamento, totalVenda, saldoDevedor.
   */
  @Input() venda!: VendaDetalheDTO;

  /**
   * Indica se o cliente tem restrição ativa (consultar antes de abrir o modal).
   * Quando true, o tipo "PARCELADA" fica desabilitado preventivamente no fluxo
   * NOVA_VENDA (evita o erro 500-05 do backend). O componente pai deve verificar
   * no cadastro do cliente (cliente.restricaoAtiva) e passar o valor aqui.
   * Se omitido (false), nenhum tipo é bloqueado preventivamente.
   */
  @Input() clienteComRestricao = false;

  /**
   * Permite exibir o tipo "Venda Mista" na seleção de tipo de venda.
   * O componente de contas a receber (baixa de parcela) passa `false`,
   * deixando disponíveis apenas "A Vista" e "Parcelada".
   */
  @Input() permitirVendaMista = true;

  /**
   * Quanto ainda pode ser lançado nesta venda, em `BAIXAR_PARCELA`.
   *
   * **Não é o mesmo que saldo devedor.** Formas parceladas geram parcelas sem
   * abater o saldo — ele só cai quando cada parcela é recebida. Validar contra
   * o saldo permitiria lançar o mesmo valor repetidamente, comprometendo mais
   * que o total da venda.
   *
   *     disponivel = totalVenda - SUM(valorPrevisto das parcelas)
   *
   * A tela pai calcula a partir das parcelas que já carrega. `null` mantém o
   * comportamento antigo (validar pelo saldo devedor) — o backend é a garantia
   * final em qualquer caso.
   *
   * Ver docs/planos/2026-09-25-plano-correcao-super-comprometimento-venda.md
   */
  @Input() valorDisponivel: number | null = null;

  // ── Outputs ──────────────────────────────────────────────────────────────
  /**
   * Emite true quando a venda/baixa foi concluída com sucesso.
   * Emite false quando o usuário cancelou sem concluir.
   */
  @Output() closeModal = new EventEmitter<boolean>();

  /** Emite o resumo do pagamento logo antes do closeModal(true) de uma NOVA_VENDA. */
  @Output() vendaEfetuada = new EventEmitter<ResumoVendaV2>();

  // ── Injeções ─────────────────────────────────────────────────────────────
  private readonly cdr           = inject(ChangeDetectorRef);
  private readonly authService   = inject(AuthService);
  private readonly toastr        = inject(ToastrService);
  private readonly vendaV2Service = inject(VendaV2Service);
  private readonly parametrosService = inject(ParametrosService);

  // ── Constantes ───────────────────────────────────────────────────────────
  readonly bandeiras = BANDEIRAS;
  readonly parcelasOpcoes = Array.from({ length: 12 }, (_, i) => i + 1);

  // ── Estado do wizard ─────────────────────────────────────────────────────
  step: FlowStep = 'tipo';

  tipoVendaSelecionado: string | null = null;   // TIPO_VENDA_* constant
  formaSelecionada: string | null = null;       // FORMA_* constant
  bandeiraCartao: BandeiraCartaoV2 | null = null;
  numParcelas = 1;
  valorEntrada = 0;
  valorABaixar = 0;

  // Previsão de entrega (apenas NOVA_VENDA)
  previsaoEntrega = '';
  dataMinEntrega  = '';
  diasCorridos    = 2;

  /** Limites do prazo (dias corridos), lidos de parametros.parametros (categoria PARAMETRO_VENDAS). */
  prazoMinimo = PRAZO_MINIMO_PADRAO;
  prazoMaximo = PRAZO_MAXIMO_PADRAO;

  // ── Estado UI ────────────────────────────────────────────────────────────
  isLoading = false;
  hasError  = false;
  errorMsg  = '';
  isDocValido = true;

  /** Modal de atualização de documento do cliente aberto (aguardando decisão do usuário). */
  docModalAberto = false;
  /** Já houve retentativa automática após atualizar o documento nesta confirmação. */
  private docRetentado = false;
  private msgDocInvalido = '';

  private idUsuario = 1;

  // ── Lifecycle ────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.idUsuario = this.authService.getUser()?.id ?? 1;
    this.resetForm();
    this.carregarLimitesPrazo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.resetForm();
    }
  }

  // ── Computed: opções de tipo de venda ────────────────────────────────────
  /**
   * Cliente com restrição ativa: bloqueia PARCELADA e VENDA MISTA preventivamente
   * no fluxo NOVA_VENDA (evita o erro 500-05 — a regra de negócio autoriza apenas
   * formas à vista para cliente restrito). Na baixa de parcela o backend não
   * valida restrição, então nada é bloqueado.
   */
  private tipoBloqueadoPorRestricao(tipo: string): boolean {
    if (this.tipoAcao !== 'NOVA_VENDA' || !this.clienteComRestricao) return false;
    return tipo === TIPO_VENDA_PARCELADA || tipo === TIPO_VENDA_MISTA;
  }

  get tiposVendaOpcoes(): TipoVendaOpcao[] {
    const tooltip = 'Cliente com restrição ativa. Somente venda à vista autorizada.';
    const opcoes: TipoVendaOpcao[] = [
      {
        value: TIPO_VENDA_A_VISTA,
        label: 'A Vista',
        descricao: 'Pagamento imediato em uma única transação',
        desativado: false,
      },
      {
        value: TIPO_VENDA_PARCELADA,
        label: 'Parcelada',
        descricao: 'Boleto, cheque ou crédito parcelado',
        desativado: this.tipoBloqueadoPorRestricao(TIPO_VENDA_PARCELADA),
        tooltipDesativado: this.tipoBloqueadoPorRestricao(TIPO_VENDA_PARCELADA) ? tooltip : undefined,
      },
      {
        value: TIPO_VENDA_MISTA,
        label: 'Venda Mista',
        descricao: 'Entrada + pagamentos futuros',
        desativado: this.tipoBloqueadoPorRestricao(TIPO_VENDA_MISTA),
        tooltipDesativado: this.tipoBloqueadoPorRestricao(TIPO_VENDA_MISTA) ? tooltip : undefined,
      },
    ];
    // Contas a receber (baixa de parcela): oferecer apenas A Vista e Parcelada.
    return this.permitirVendaMista
      ? opcoes
      : opcoes.filter(o => o.value !== TIPO_VENDA_MISTA);
  }

  // ── Computed: formas disponíveis conforme tipo selecionado ───────────────
  get formasDisponiveis(): FormaOpcao[] {
    let formas: string[] = [];

    if (this.tipoVendaSelecionado === TIPO_VENDA_A_VISTA) {
      formas = FORMAS_A_VISTA;
    } else if (this.tipoVendaSelecionado === TIPO_VENDA_PARCELADA) {
      formas = FORMAS_PARCELADA;
    } else if (this.tipoVendaSelecionado === TIPO_VENDA_MISTA) {
      formas = FORMAS_VENDA_MISTA;
    } else if (this.tipoAcao === 'BAIXAR_PARCELA') {
      // BAIXAR_PARCELA usa mesmas formas que MISTA (sem restrição no backend)
      formas = FORMAS_VENDA_MISTA;
    }

    return formas.map(f => ({ value: f, label: this.labelForma(f) }));
  }

  get requiresBandeira(): boolean {
    return this.formaSelecionada !== null && FORMAS_COM_BANDEIRA.includes(this.formaSelecionada);
  }

  get isParcelado(): boolean {
    return this.formaSelecionada !== null && FORMAS_PARCELADAS.includes(this.formaSelecionada);
  }

  /**
   * Passo de "entrada" só existe na criação de venda mista.
   * No BAIXAR_PARCELA o valor é informado direto no passo de confirmação (valorABaixar).
   */
  get precisaEntrada(): boolean {
    return this.tipoAcao === 'NOVA_VENDA' && this.tipoVendaSelecionado === TIPO_VENDA_MISTA;
  }

  // Saldo calculado em tempo real na VENDA MISTA
  get saldoAReceber(): number {
    if (!this.orcamento) return 0;
    return Math.max(0, this.orcamento.valorTotal - this.valorEntrada);
  }

  // Novo saldo devedor após baixa
  get novoSaldoDevedor(): number {
    if (!this.venda) return 0;
    return Math.max(0, this.venda.saldoDevedor - this.valorABaixar);
  }

  /**
   * Limite efetivo para o valor a lançar. Usa `valorDisponivel` quando a tela
   * pai o informa; sem ele, cai no saldo devedor (comportamento antigo).
   */
  get limiteLancamento(): number {
    if (this.valorDisponivel !== null) return this.valorDisponivel;
    return this.venda?.saldoDevedor ?? 0;
  }

  /**
   * Só faz sentido mostrar as duas grandezas quando elas divergem — o que
   * acontece a partir do primeiro pagamento parcelado em aberto.
   */
  get disponivelDivergeDoSaldo(): boolean {
    return this.valorDisponivel !== null
      && !!this.venda
      && Math.abs(this.valorDisponivel - this.venda.saldoDevedor) >= 0.01;
  }

  // ── Navegação do wizard ───────────────────────────────────────────────────
  selecionarTipo(tipo: string): void {
    this.tipoVendaSelecionado = tipo;
    this.formaSelecionada = null;
    this.bandeiraCartao = null;
    this.numParcelas = 1;
    this.hasError = false;

    // BAIXAR_PARCELA não tem passo de tipo (mas mantemos por segurança)
    // A seleção vai direto para o passo de forma
    this.step = 'forma';
  }

  selecionarForma(forma: string): void {
    this.formaSelecionada = forma;
    this.bandeiraCartao = null;
    this.hasError = false;

    // Sempre pedimos parcelas se a forma for parcelada
    if (this.isParcelado) {
      this.step = 'parcelas';
      return;
    }

    if (this.requiresBandeira) {
      this.step = 'bandeira';
      return;
    }

    // VENDA MISTA (nova venda): pede valor de entrada
    if (this.precisaEntrada) {
      this.step = 'entrada';
      return;
    }

    this.step = 'confirmacao';
  }

  confirmarParcelas(): void {
    if (this.numParcelas < 1 || this.numParcelas > 12) {
      this.hasError = true;
      this.errorMsg = 'Informe um número de parcelas entre 1 e 12.';
      return;
    }
    this.hasError = false;

    if (this.requiresBandeira) {
      this.step = 'bandeira';
      return;
    }

    if (this.precisaEntrada) {
      this.step = 'entrada';
      return;
    }

    this.step = 'confirmacao';
  }

  confirmarBandeira(): void {
    if (!this.bandeiraCartao) {
      this.hasError = true;
      this.errorMsg = 'Selecione a bandeira do cartão.';
      return;
    }
    this.hasError = false;

    if (this.precisaEntrada) {
      this.step = 'entrada';
      return;
    }

    this.step = 'confirmacao';
  }

  confirmarEntrada(): void {
    if (!this.orcamento) return;
    if (this.valorEntrada <= 0 || this.valorEntrada >= this.orcamento.valorTotal) {
      this.hasError = true;
      this.errorMsg = 'Valor de entrada deve ser maior que 0 e menor que o valor total.';
      return;
    }
    this.hasError = false;
    this.step = 'confirmacao';
  }

  voltarStep(): void {
    this.hasError = false;

    if (this.step === 'forma') { this.step = 'tipo'; return; }

    if (this.step === 'parcelas') { this.step = 'forma'; return; }

    if (this.step === 'bandeira') {
      this.step = this.isParcelado ? 'parcelas' : 'forma';
      return;
    }

    if (this.step === 'entrada') {
      this.step = this.requiresBandeira ? 'bandeira' : (this.isParcelado ? 'parcelas' : 'forma');
      return;
    }

    if (this.step === 'confirmacao') {
      if (this.precisaEntrada) {
        this.step = 'entrada';
      } else if (this.requiresBandeira) {
        this.step = 'bandeira';
      } else if (this.isParcelado) {
        this.step = 'parcelas';
      } else {
        this.step = 'forma';
      }
    }
  }

  // ── Submissão ────────────────────────────────────────────────────────────
  confirmar(): void {
    if (this.isLoading || this.docModalAberto) return;
    this.docRetentado = false;
    if (this.tipoAcao === 'NOVA_VENDA') {
      this.efetuarNovaVenda();
    } else {
      this.efetuarBaixaParcela();
    }
  }

  private efetuarNovaVenda(): void {
    if (!this.validarCamposComuns()) return;

    if (!this.previsaoEntrega) {
      this.hasError = true;
      this.errorMsg = 'Data de entrega é obrigatória.';
      return;
    }

    if (this.previsaoEntrega < this.dataMinEntrega) {
      this.hasError = true;
      this.errorMsg = 'A data de entrega não pode ser menor que hoje.';
      return;
    }

    if (this.tipoVendaSelecionado === TIPO_VENDA_MISTA && this.valorEntrada <= 0) {
      this.hasError = true;
      this.errorMsg = 'Valor de entrada é obrigatório para venda mista.';
      return;
    }

    this.isLoading = true;
    this.hasError  = false;

    const dto = this.montarDTO();
    this.vendaV2Service.efetuarVenda(dto).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastr.success('Venda criada com sucesso.');
        this.vendaEfetuada.emit({
          tipoVendaPagamento: dto.tipoVendaPagamento,
          formaPagamento:     dto.formaPagamento,
          bandeiraCartao:     dto.bandeiraCartao,
          numParcelas:        dto.numParcelas,
          valorEntrada:       dto.valorEntrada,
          previsaoEntrega:    dto.previsaoEntrega,
        });
        this.fecharModal(true);
      },
      error: (err) => this.tratarErro(err)
    });
  }

  private efetuarBaixaParcela(): void {
    if (this.valorABaixar <= 0 || this.valorABaixar > this.limiteLancamento) {
      this.toastr.warning(
        'Valor a abater deve ser maior que 0 e menor ou igual ao disponível para '
        + `lançamento (${formatarMoedaBRL(this.limiteLancamento)}).`
      );
      return;
    }

    if (!this.validarCamposComuns()) return;

    this.isLoading = true;
    this.hasError  = false;

    const dto = this.montarDTO();
    this.vendaV2Service.baixarParcela(dto).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastr.success('Pagamento registrado com sucesso.');
        this.fecharModal(true);
      },
      error: (err) => this.tratarErro(err)
    });
  }

  private validarCamposComuns(): boolean {
    if (!this.tipoVendaSelecionado) {
      this.hasError = true;
      this.errorMsg = 'Selecione o tipo de venda.';
      this.step = 'tipo';
      return false;
    }

    if (!this.formaSelecionada) {
      this.hasError = true;
      this.errorMsg = 'Selecione a forma de pagamento.';
      this.step = 'forma';
      return false;
    }

    if (this.requiresBandeira && !this.bandeiraCartao) {
      this.hasError = true;
      this.errorMsg = 'Selecione a bandeira do cartão.';
      this.step = 'bandeira';
      return false;
    }

    return true;
  }

  private montarDTO(): EfetuarVendaDTO {
    const isMista = this.tipoVendaSelecionado === TIPO_VENDA_MISTA;
    const isBaixa = this.tipoAcao === 'BAIXAR_PARCELA';

    // formaPagamento enviado segue a spec: a forma selecionada já é a string do backend
    const formaPagamento = this.formaSelecionada ?? '';

    // tipoVendaPagamento: BAIXAR_PARCELA usa o tipo selecionado pelo usuário
    const tipoVendaPagamento = isBaixa && !this.tipoVendaSelecionado
      ? TIPO_VENDA_A_VISTA
      : (this.tipoVendaSelecionado ?? TIPO_VENDA_A_VISTA);

    return {
      idOrcamento:       isBaixa ? this.venda?.idOrcamento : this.orcamento?.id,
      idCliente:         isBaixa ? 0 : (this.orcamento?.idCliente ?? 0),
      formaPagamento,
      bandeiraCartao:    this.bandeiraCartao,
      numParcelas:       this.isParcelado ? this.numParcelas : 1,
      idUsuario:         this.idUsuario,
      previsaoEntrega:   isBaixa ? '' : this.previsaoEntrega,
      valorEntrada:      isBaixa
                           ? this.valorABaixar
                           : (isMista ? this.valorEntrada : null),
      tipoVendaPagamento,
    };
  }

  private tratarErro(err: any): void {
    const erro: ErrorModel = err?.error ?? {};
    const code: string = erro.code ?? '';

    if (code === ERRO_CPF_CNPJ_INVALIDO && this.tipoAcao === 'NOVA_VENDA' && !this.docRetentado) {
      // Abre o modal de atualização; sem toast/mensagem até o usuário decidir.
      // isLoading permanece true para impedir nova submissão.
      this.msgDocInvalido = erro.message
        ?? 'O documento cadastrado para este cliente é inválido. Por favor, atualize o cadastro antes de efetuar a venda.';
      this.hasError = false;
      this.errorMsg = '';
      this.docModalAberto = true;
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = false;
    this.hasError  = true;
    this.cdr.detectChanges();

    switch (code) {
      case ERRO_CPF_CNPJ_INVALIDO:
        this.isDocValido = false;
        this.errorMsg = erro.message
          ?? 'O documento cadastrado para este cliente é inválido. Por favor, atualize o cadastro antes de efetuar a venda.';
        break;

      case ERRO_CLIENTE_RESTRICAO:
        this.errorMsg = erro.message
          ?? 'Restrição ativa. Somente autorizado venda via Débito, Crédito à Vista, Pix ou Espécie.';
        break;

      case ERRO_ACAO_NAO_PERMITIDA:
        this.errorMsg = erro.message ?? 'Ação não permitida para o status atual do registro.';
        break;

      case ERRO_FUNCAO_NAO_IMPL:
        this.errorMsg = erro.message ?? 'Função não implementada.';
        break;

      case ERRO_ADICIONAR_PAGAMENTO:
        this.errorMsg = erro.message
          ?? 'Não foi possível adicionar pagamento. Por favor, contate o administrador do sistema.';
        break;

      case ERRO_EFETIVAR_VENDA:
        this.errorMsg = erro.message
          ?? 'Não foi possível efetivar esta venda. Por favor, contate o administrador do sistema.';
        break;

      case ERRO_BAIXAR_PARCELA:
        this.errorMsg = erro.message
          ?? 'Não foi possível baixar a parcela do cartão. Por favor, contate o administrador do sistema.';
        break;

      default:
        this.errorMsg = erro.message ?? 'Ocorreu um erro ao processar a venda. Tente novamente.';
    }

    this.toastr.error(this.errorMsg);
    this.cdr.detectChanges();
  }

  // ── Modal de documento do cliente ─────────────────────────────────────────
  onDocumentoSalvo(): void {
    this.docModalAberto = false;
    this.isDocValido = true;
    this.hasError = false;
    this.errorMsg = '';
    this.docRetentado = true; // no máximo uma retentativa automática
    this.isLoading = false;
    this.efetuarNovaVenda();
    this.cdr.detectChanges();
  }

  onDocumentoCancelado(): void {
    this.docModalAberto = false;
    this.isLoading = false;
    this.isDocValido = false;
    this.hasError = true;
    this.errorMsg = this.msgDocInvalido;
    this.toastr.error(this.errorMsg);
    this.cdr.detectChanges();
  }

  // ── Previsão de entrega ───────────────────────────────────────────────────
  onDiasCorridosChange(): void {
    this.diasCorridos    = this.normalizarDias(this.diasCorridos);
    this.previsaoEntrega = this.somarDias(this.dataMinEntrega, this.diasCorridos);
  }

  onPrevisaoEntregaChange(): void {
    if (!this.previsaoEntrega) return;
    if (this.previsaoEntrega < this.dataMinEntrega) {
      this.previsaoEntrega = this.dataMinEntrega;
    }
    const diff = this.diferencaDias(this.dataMinEntrega, this.previsaoEntrega);
    this.diasCorridos = this.normalizarDias(diff);
  }

  private carregarLimitesPrazo(): void {
    this.parametrosService.getParametrosByCategorias([CATEGORIA_PARAMETRO_VENDAS]).subscribe({
      next: (parametros) => {
        const prazo = parametros.find(p => p.categoria === CATEGORIA_PARAMETRO_VENDAS);
        const min = this.parseDias(prazo?.valorMinimo);
        const max = this.parseDias(prazo?.valorMaximo);
        // Parâmetro ausente ou incoerente: mantém os limites padrão.
        if (min !== null && max !== null && min <= max) {
          this.prazoMinimo = min;
          this.prazoMaximo = max;
          this.onDiasCorridosChange();
        }
        this.cdr.detectChanges();
      },
      error: () => this.cdr.detectChanges()
    });
  }

  private parseDias(valor: string | undefined): number | null {
    if (valor === undefined || valor === null || String(valor).trim() === '') return null;
    const numero = Number(valor);
    return Number.isFinite(numero) && numero >= 0 ? Math.trunc(numero) : null;
  }

  private normalizarDias(dias: number): number {
    const v = Number.isFinite(Number(dias)) ? Number(dias) : this.prazoMinimo;
    if (v < this.prazoMinimo) return this.prazoMinimo;
    if (v > this.prazoMaximo) return this.prazoMaximo;
    return Math.trunc(v);
  }

  /** Soma N dias a uma string 'yyyy-MM-dd' sem criar Date a partir de string (evita UTC shift) */
  private somarDias(base: string, dias: number): string {
    const [y, m, d] = base.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + dias);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
  }

  private diferencaDias(inicio: string, fim: string): number {
    const ms = new Date(`${fim}T00:00:00`).getTime() - new Date(`${inicio}T00:00:00`).getTime();
    return Math.max(0, Math.floor(ms / 86400000));
  }

  // ── Barra de progresso ───────────────────────────────────────────────────
  get progressSteps(): ProgressItem[] {
    const steps: ProgressItem[] = [];

    // Passo "tipo" existe nos dois fluxos (usuário escolhe entre A Vista / Parcelada / Mista)
    steps.push({ key: 'tipo', label: 'Tipo' });

    steps.push({ key: 'forma', label: 'Forma' });

    if (this.isParcelado) {
      steps.push({ key: 'parcelas', label: 'Parcelas' });
    }

    if (this.requiresBandeira) {
      steps.push({ key: 'bandeira', label: 'Bandeira' });
    }

    if (this.precisaEntrada) {
      steps.push({ key: 'entrada', label: 'Entrada' });
    }

    steps.push({ key: 'confirmacao', label: 'Confirmar' });
    return steps;
  }

  get currentStepIndex(): number {
    return this.progressSteps.findIndex(s => s.key === this.step);
  }

  isStepAtivo(key: FlowStep): boolean {
    return this.step === key;
  }

  isStepConcluido(key: FlowStep): boolean {
    const idx = this.progressSteps.findIndex(s => s.key === key);
    return idx !== -1 && idx < this.currentStepIndex;
  }

  getStepNumero(key: FlowStep): number {
    const idx = this.progressSteps.findIndex(s => s.key === key);
    return idx >= 0 ? idx + 1 : 0;
  }

  // ── Labels para confirmação ──────────────────────────────────────────────
  get labelTipoVenda(): string {
    return this.tiposVendaOpcoes.find(t => t.value === this.tipoVendaSelecionado)?.label ?? '';
  }

  get labelFormaSelected(): string {
    return this.labelForma(this.formaSelecionada ?? '');
  }

  private labelForma(forma: string): string {
    const mapa: Record<string, string> = {
      'CREDITO A VISTA':   'Crédito à Vista',
      'CREDITO PARCELADO': 'Crédito Parcelado',
      'DEBITO':            'Débito',
      'ESPECIE':           'Espécie',
      'PIX':               'Pix',
      'BOLETO':            'Boleto',
      'CHEQUE':            'Cheque',
      'VENDA MISTA':       'Venda Mista',
    };
    return mapa[forma] ?? forma;
  }

  // ── Fechar modal ─────────────────────────────────────────────────────────
  fecharModal(concluido = false): void {
    if (this.isLoading) return;
    this.resetForm();
    this.closeModal.emit(concluido);
  }

  private resetForm(): void {
    // Ambos os fluxos começam no passo "tipo" (escolha entre A Vista / Parcelada / Mista)
    this.step                  = 'tipo';
    this.tipoVendaSelecionado  = null;
    this.formaSelecionada      = null;
    this.bandeiraCartao        = null;
    this.numParcelas           = 1;
    this.valorEntrada          = 0;
    this.valorABaixar          = 0;
    this.diasCorridos          = 2;
    this.dataMinEntrega        = todayLocalDateString();
    this.previsaoEntrega       = localDateStringPlusDays(2);
    this.isLoading             = false;
    this.hasError              = false;
    this.errorMsg              = '';
    this.isDocValido           = true;
    this.docModalAberto        = false;
    this.docRetentado          = false;
    this.msgDocInvalido        = '';
  }

  // Expõe constantes ao template
  readonly TIPO_VENDA_A_VISTA   = TIPO_VENDA_A_VISTA;
  readonly TIPO_VENDA_PARCELADA = TIPO_VENDA_PARCELADA;
  readonly TIPO_VENDA_MISTA     = TIPO_VENDA_MISTA;
  readonly FORMA_CREDITO_PARCELADO = FORMA_CREDITO_PARCELADO;
}
